import * as Dialog from '@radix-ui/react-dialog'
import { ChatPanel } from '../ChatPanel.jsx'
import { SearchTrigger } from '../SearchTrigger.jsx'
import { OverlaySplitSurface } from './OverlaySplitSurface.jsx'
import { useChatLayout } from '../../../context/ChatLayoutContext.jsx'
import { cn } from '../../../lib/cn.js'
import {
  clampSlideOutPanelWidthPx,
  normalizeSlideOutPanelMaxHeightPx,
} from '../../../lib/slideOutPanelLayout.js'
import { resolveButtonBorderRadius } from '@shared/launcherButtonIcons.jsx'
import { sanitizeWidgetBubbleIconSvg } from '../../../lib/sanitizeBubbleIcon.js'
import { IconButton } from '../../ui/IconButton.jsx'
import { OchatbotBubbleIcon } from '../OchatbotBubbleIcon.jsx'
import { LauncherButtonContent } from '../LauncherButtonContent.jsx'

/**
 * Resolve which in-page trigger to show. `widgetType` (unified envelope)
 * wins; the legacy flat `layout` field is a fallback. Anything unknown
 * falls back to the search box, which is the most common in-page surface.
 */
function resolveInjectedTriggerKind(conf) {
  const wt = String(conf?.widgetType || '').trim().toLowerCase()
  if (wt === 'button') return 'button'
  if (wt === 'bubble' || wt === 'icon') return 'bubble'
  if (wt === 'search') return 'search'
  const layout = String(conf?.layout || '').trim().toLowerCase()
  if (layout === 'button') return 'button'
  if (layout === 'bubble') return 'bubble'
  return 'search'
}

/**
 * Canonical open behavior for an in-page trigger: `expand` (inline,
 * pushes page content down), `overlay` (centered modal), or `slide-out`
 * (side drawer). Defaults to `overlay`.
 */
function canonicalInjectedDisplayMode(conf) {
  const raw = String(conf?.displayMode || conf?.ometricsAI?.displayMode || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
  if (raw.includes('slide') && raw.includes('out')) return 'slide-out'
  if (raw === 'slideout' || raw === 'mobile') return 'slide-out'
  if (raw === 'expand') return 'expand'
  if (raw === 'overlay') return 'overlay'
  return 'overlay'
}

function isInteractiveSearch(conf) {
  return String(conf?.searchBoxType || '').toLowerCase() === 'interactive'
}

/**
 * In-page ("injected") layout. Renders a compact trigger inside the
 * customer's page — a search box, a button, or the Ometrics bubble icon
 * (`widgetType`) — and opens the chat surface on click in the configured
 * `displayMode`: `overlay` (modal), `expand` (inline), or `slide-out`
 * (drawer). Mobile always promotes to slide-out. v1 parity: `ChatContainer`
 * + `SearchLayout`/`ButtonLayout`/bubble icon dispatching to
 * Overlay/Expand/SlideOut chat layouts.
 *
 * `layout === 'open'` keeps the legacy full-bleed embedded panel (no
 * trigger, always open) for dedicated support pages.
 */
export function InjectedLayout({
  conf,
  messages,
  isTyping,
  onSend,
  ochatbot,
  container,
  panelContainer,
  onLeadFormSubmit,
  onOrderStatusResult,
  ...panelRest
}) {
  const { open, openChat, closeChat, toggleChat } = useChatLayout()
  const isMobile = conf?.isMobile === true
  const triggerKind = resolveInjectedTriggerKind(conf)
  const mode = isMobile ? 'slide-out' : canonicalInjectedDisplayMode(conf)
  const isOpenLayout =
    String(conf?.layout || '').trim().toLowerCase() === 'open'
  const isInline = String(conf?.displayType || '').toLowerCase() === 'inline'
  const interactive = isInteractiveSearch(conf)

  const edge =
    String(conf?.slideOutPanelEdge || 'right').toLowerCase() === 'left'
      ? 'left'
      : 'right'
  const slideOutPanelWidthPx = clampSlideOutPanelWidthPx(
    conf?.slideOutPanelWidth ?? conf?.ometricsAI?.slideOutPanelWidth,
  )
  const desktopMaxHeightPx = normalizeSlideOutPanelMaxHeightPx(
    conf?.slideOutPanelMaxHeight ?? conf?.ometricsAI?.slideOutPanelMaxHeight,
  )
  const smSlideOutCappedHeight = mode === 'slide-out' && desktopMaxHeightPx != null

  const brand = conf?.widgetButtonColor?.trim?.() || 'var(--color-brand)'
  const brandFg =
    conf?.widgetButtonTextColor?.trim?.() || 'var(--color-brand-foreground)'

  // --- Embedded full panel (legacy `layout: 'open'`) -----------------------
  if (isOpenLayout) {
    return (
      <div className="ometrics-injected h-full min-h-[400px] w-full">
        <ChatPanel
          conf={conf}
          messages={messages}
          isTyping={isTyping}
          onSend={onSend}
          ochatbot={ochatbot}
          showClose={false}
          onLeadFormSubmit={onLeadFormSubmit}
          onOrderStatusResult={onOrderStatusResult}
          {...panelRest}
        />
      </div>
    )
  }

  // --- Trigger activation --------------------------------------------------
  const activateChat = () => {
    openChat()
  }

  const searchTrigger = (
    <SearchTrigger
      conf={conf}
      open={open}
      interactive={interactive}
      isTyping={isTyping}
      onOpenChat={activateChat}
      onToggleChat={toggleChat}
      onSend={onSend}
      formClassName="w-full max-w-none"
    />
  )

  const buttonRadius = resolveButtonBorderRadius(conf?.buttonBorderRadius)
  const buttonBg = conf?.buttonColor?.trim?.() || brand
  const buttonFg = conf?.buttonTextColor?.trim?.() || brandFg
  const buttonBorder = conf?.buttonBorderColor?.trim?.() || buttonBg
  const buttonText = conf?.buttonText?.trim?.() || conf?.widgetTitle || 'Chat with AI'

  const buttonTrigger = (
    <button
      type="button"
      onClick={activateChat}
      style={{
        backgroundColor: buttonBg,
        color: buttonFg,
        borderColor: buttonBorder,
        borderRadius: buttonRadius,
      }}
      className={cn(
        'inline-flex items-center justify-center gap-2 border border-solid shadow-sm transition-opacity hover:opacity-90',
        isInline ? 'min-w-[120px] px-4 py-2 text-sm' : 'min-w-[200px] px-5 py-2.5 text-[15px]',
        'font-medium',
      )}
    >
      <LauncherButtonContent conf={conf} />
    </button>
  )

  const bubbleBg =
    conf?.widget_bubble_background_color?.trim?.() ||
    conf?.buttonColor?.trim?.() ||
    conf?.widgetButtonColor?.trim?.() ||
    'var(--color-brand)'
  const bubbleFg =
    conf?.widget_bubble_color?.trim?.() ||
    conf?.buttonTextColor?.trim?.() ||
    conf?.widgetButtonTextColor?.trim?.() ||
    'var(--color-brand-foreground)'
  const customBubbleSvg = sanitizeWidgetBubbleIconSvg(
    conf?.widgetBubbleIcon || conf?.widget_bubble_icon,
  )

  const bubbleTrigger = (
    <button
      type="button"
      onClick={activateChat}
      aria-label={conf?.widgetTitle || 'Open chat'}
      className="inline-flex h-[60px] w-[60px] items-center justify-center rounded-full shadow-[var(--shadow-widget)] transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]"
    >
      {customBubbleSvg ? (
        <span
          className="flex h-[60px] w-[60px] items-center justify-center [&_svg]:max-h-[60px] [&_svg]:max-w-[60px]"
          // Admin-configured SVG (same trust model as the floating bubble launcher).
          dangerouslySetInnerHTML={{ __html: customBubbleSvg }}
        />
      ) : (
        <OchatbotBubbleIcon
          className="h-[60px] w-[60px]"
          bubbleFill={bubbleFg}
          bubbleBackground={bubbleBg}
        />
      )}
    </button>
  )

  const trigger =
    triggerKind === 'button'
      ? buttonTrigger
      : triggerKind === 'bubble'
        ? bubbleTrigger
        : searchTrigger

  const shellClass = cn(
    'ometrics-injected-trigger flex',
    triggerKind === 'search'
      ? isInline
        ? 'inline-flex min-w-[320px] max-w-[450px] flex-col'
        : 'w-[min(100%,500px)] max-w-[500px] flex-col'
      : isInline
        ? 'inline-flex'
        : 'flex-col items-center',
  )

  const triggerRow = (
    <div
      className={cn(
        'ometrics-injected',
        isInline ? 'inline-flex' : 'flex w-full justify-center',
      )}
    >
      <div className={shellClass}>{trigger}</div>
    </div>
  )

  const splitChatPanel = (
    <ChatPanel
      {...panelRest}
      conf={conf}
      messages={messages}
      isTyping={isTyping}
      onSend={onSend}
      onClose={closeChat}
      ochatbot={ochatbot}
      className={cn(
        'min-h-0 flex-1 border-0 rounded-none',
        panelRest.className,
      )}
      embedSuggestions={false}
      showClose={false}
      headerClassName="pr-14"
      onLeadFormSubmit={onLeadFormSubmit}
      onOrderStatusResult={onOrderStatusResult}
    />
  )

  // --- Expand mode: inline panel that pushes page content down -------------
  if (mode === 'expand') {
    if (!open) return triggerRow
    return (
      <div className="ometrics-injected-expand flex w-full justify-center">
        <div
          className={cn(
            'relative flex w-full max-w-[1200px] flex-col overflow-hidden',
            'h-[min(620px,80vh)] rounded-2xl border border-[var(--color-border)]',
            'bg-[var(--color-surface)] shadow-[var(--shadow-widget)]',
          )}
        >
          <OverlaySplitSurface
            conf={conf}
            messages={messages}
            onClose={closeChat}
            onSend={onSend}
            ochatbot={ochatbot}
            onLeadFormSubmit={onLeadFormSubmit}
            onOrderStatusResult={onOrderStatusResult}
          >
            {splitChatPanel}
          </OverlaySplitSurface>
        </div>
      </div>
    )
  }

  // --- Overlay / slide-out: trigger stays in-page, chat opens in a portal --
  const slideOut = mode === 'slide-out'
  const portalContainer = panelContainer || container

  const overlayBackdrop = slideOut
    ? cn(
        'pointer-events-none fixed inset-0 z-[2147483645] bg-transparent',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=open]:fade-in data-[state=closed]:fade-out',
      )
    : cn(
        'fixed inset-0 z-[2147483645] bg-black/50',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=open]:fade-in data-[state=closed]:fade-out',
      )

  const contentClass = slideOut
    ? cn(
        'fixed z-[2147483647] flex min-h-0 w-full flex-col overflow-hidden',
        'top-0 bottom-0',
        edge === 'right' && 'right-0 border-l border-[var(--color-border)]',
        edge === 'left' && 'left-0 border-r border-[var(--color-border)]',
        'sm:w-[min(100vw,var(--ometrics-slide-out-panel-width))] sm:max-w-[min(100vw,var(--ometrics-slide-out-panel-width))]',
        edge === 'right' && 'sm:rounded-l-2xl',
        edge === 'left' && 'sm:rounded-r-2xl',
        smSlideOutCappedHeight &&
          'sm:top-auto sm:bottom-20 sm:h-[min(var(--ometrics-slide-out-panel-max-height),calc(100dvh-5rem))] sm:max-h-[min(var(--ometrics-slide-out-panel-max-height),calc(100dvh-5rem))]',
        'shadow-[var(--shadow-widget)]',
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:duration-300 data-[state=closed]:duration-300',
        edge === 'right' &&
          'data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right',
        edge === 'left' &&
          'data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left',
        'ease-[cubic-bezier(0.4,0,0.2,1)]',
      )
    : cn(
        'fixed left-1/2 top-1/2 z-[2147483647] flex -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden',
        'h-[100dvh] w-full max-h-[100dvh] max-w-[1200px] rounded-none',
        'md:h-[min(620px,80vh)] md:w-[min(1200px,calc(100vw-2rem))] md:max-h-[620px] md:rounded-2xl',
        'border border-[var(--color-border)] bg-[var(--color-surface)]',
        'shadow-[var(--shadow-widget)]',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95',
      )

  return (
    <Dialog.Root
      open={open}
      modal={false}
      onOpenChange={(next) => (next ? openChat() : closeChat())}
    >
      {triggerRow}

      <Dialog.Portal container={portalContainer}>
        <Dialog.Overlay className={overlayBackdrop} />
        <Dialog.Content
          aria-describedby={undefined}
          className={contentClass}
          style={
            slideOut
              ? {
                  ['--ometrics-slide-out-panel-width']: `${slideOutPanelWidthPx}px`,
                  ...(smSlideOutCappedHeight
                    ? {
                        ['--ometrics-slide-out-panel-max-height']: `${desktopMaxHeightPx}px`,
                      }
                    : {}),
                }
              : undefined
          }
        >
          <Dialog.Title className="sr-only">
            {conf?.widgetTitle || 'Chat'}
          </Dialog.Title>
          {slideOut ? (
            <ChatPanel
              conf={conf}
              messages={messages}
              isTyping={isTyping}
              onSend={onSend}
              onClose={closeChat}
              ochatbot={ochatbot}
              className="min-h-0 flex-1 basis-0"
              onLeadFormSubmit={onLeadFormSubmit}
              onOrderStatusResult={onOrderStatusResult}
              {...panelRest}
            />
          ) : (
            <OverlaySplitSurface
              conf={conf}
              messages={messages}
              onClose={closeChat}
              onSend={onSend}
              ochatbot={ochatbot}
              onLeadFormSubmit={onLeadFormSubmit}
              onOrderStatusResult={onOrderStatusResult}
            >
              {splitChatPanel}
            </OverlaySplitSurface>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
